import { useRef } from 'react'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { useIsMountedRef, useVariableRef } from '../../../../../hooks'
import { getItemsFromIdwellApiResponse } from '../../../../../utils/api'
import { getRequestBoardColumnItems } from '../../RequestBoard.api'
import {
  getRequestBoardItemRequestIds,
  isRequestBoardItemRequestLatest,
} from '../../RequestBoard.utils'

import {
  getRequestBoardColumnGenerations,
  getRequestBoardColumnPageGenerations,
  getRequestBoardItem,
  getRequestBoardItemParams,
  getRequestBoardItemPlacement,
  getRequestBoardUnchangedColumns,
  isRequestBoardColumnReloadNeeded,
  isRequestBoardItemPlacementKept,
} from './useRequestBoardItemSync.utils'

export function useRequestBoardItemSync({
  columns,
  filterValues,
  onLoadItems,
  onReload,
}) {
  const requestIdRef = useRef(0)
  const itemRequestIdsRef = useRef(EMPTY_OBJECT)
  const itemActionPageGenerationsRef = useRef(EMPTY_OBJECT)

  const columnsRef = useVariableRef(columns)
  const onReloadRef = useVariableRef(onReload)

  const isMountedRef = useIsMountedRef()

  function applyReloadedItem({
    generations,
    id,
    pageGenerations,
    placement,
    response,
  }) {
    const items = getItemsFromIdwellApiResponse(response)

    const item = getRequestBoardItem({ id, items })

    const unchangedColumns = getRequestBoardUnchangedColumns({
      columns: columnsRef.current,
      generations,
    })

    if (
      isRequestBoardItemPlacementKept({
        columns: columnsRef.current,
        id,
        placement,
        unchangedColumns,
      })
    ) {
      unchangedColumns.forEach(column => {
        if (
          isRequestBoardColumnReloadNeeded({
            column,
            id,
            item,
            pageGenerations,
          })
        ) {
          column.handleReload()
        } else {
          column.handleSyncItem({ id, item })
        }
      })
    } else {
      unchangedColumns.forEach(column => column.handleUpdateItem(item))
    }

    onLoadItems(items)
  }

  function handleStartItemAction(id) {
    itemActionPageGenerationsRef.current = {
      ...itemActionPageGenerationsRef.current,
      [id]: getRequestBoardColumnPageGenerations(columnsRef.current),
    }
  }

  async function handleReloadItem(id) {
    requestIdRef.current += 1

    const requestId = requestIdRef.current

    itemRequestIdsRef.current = getRequestBoardItemRequestIds({
      ids: [id],
      requestId,
      requestIds: itemRequestIdsRef.current,
    })

    const generations = getRequestBoardColumnGenerations(columnsRef.current)

    const pageGenerations =
      itemActionPageGenerationsRef.current[id] ??
      getRequestBoardColumnPageGenerations(columnsRef.current)

    const placement = getRequestBoardItemPlacement({
      columns: columnsRef.current,
      id,
    })

    return getRequestBoardColumnItems(
      getRequestBoardItemParams({ filterValues, id })
    )
      .then(response => {
        if (
          isMountedRef.current &&
          isRequestBoardItemRequestLatest({
            id,
            requestId,
            requestIds: itemRequestIdsRef.current,
          })
        ) {
          applyReloadedItem({
            generations,
            id,
            pageGenerations,
            placement,
            response,
          })
        }
      })
      .catch(() => {
        if (
          isMountedRef.current &&
          isRequestBoardItemRequestLatest({
            id,
            requestId,
            requestIds: itemRequestIdsRef.current,
          })
        ) {
          onReloadRef.current()
        }
      })
  }

  return { handleReloadItem, handleStartItemAction }
}
